import { addonAmount, offeredList } from '@/lib/options'
import type { OptionGroup, Product, Tier } from '@/types'
import { urgentRowDrafts, urgentRowsPayload, urgentRowsProblem, type UrgentRowDraft } from '@/components/admin/UrgentRatesEditor'

/**
 * One gallery slot in the editor. Existing rows round-trip the server's
 * id/path/key; new picks hold the File locally (object-URL preview) and only
 * upload inside the save mutation, so Cancel never leaves an orphan file.
 * `uploaded` memoizes a finished upload so retrying a failed save does not
 * upload the same file twice.
 */
export type GalleryItem =
  | { kind: 'existing'; id: number; image_path: string; image_key: string | null }
  | { kind: 'new'; localId: string; file: File; previewUrl: string; uploaded?: { image_path: string; image_key: string } }

export const MAX_IMAGES = 8

export interface Draft {
  id?: number
  name: string
  category: string
  sizes: string
  weight_grams: string
  images: GalleryItem[]
  active: boolean
  /** Ticked values per Product Options group, keyed by the group's key. */
  options: Record<string, string[]>
  /**
   * Add-on RM per piece per ticked value per tier (Duncan 2026-10-17): group
   * key, then value, then tier id, as input strings; blank = no extra charge
   * at that tier.
   */
  addons: Record<string, Record<string, Record<string, string>>>
  /** Group keys switched off for this product; every other group with ticked values is compulsory. */
  optional: string[]
  allow_personalization: boolean
  /** RM per piece added when the customer chooses name & number, as an input string; blank = free. */
  personalization_price: string
  /**
   * How a rush is priced (client 2026-10-06): the standard table on the SLA
   * settings page, this product's own rows, or not offered at all.
   */
  urgent_mode: 'standard' | 'custom' | 'off'
  /** The product's own rush rows; the complete set, replaced on save. */
  urgent_tiers: UrgentRowDraft[]
  tier_prices: Record<number, string>
  /**
   * Sizes priced apart from the rest (Duncan 2026-10-29: kids 22-32), each
   * with a price per tier; a size in no group pays tier_prices.
   */
  size_groups: SizeGroupDraft[]
  /**
   * Prices agreed with individual customers, which beat their tier price.
   * The list is the complete set for this product - a row removed here is
   * deleted on save, which is why the payload always carries the key.
   */
  customer_prices: CustomerPriceDraft[]
}

/**
 * One special customer row in the editor. `price` is an input string, blank
 * keeping their tier price. `addons` are their own option add-ons (group key,
 * then value), blank following their tier and 0 meaning free.
 */
export interface CustomerPriceDraft {
  user_id: number
  label: string
  /** Their tier, for the tier add-on shown beside each input; null = entry tier. */
  tierId: number | null
  price: string
  addons: Record<string, Record<string, string>>
  /** Their own price per size group, by group name; blank follows the tier's group price. */
  groupPrices: Record<string, string>
  /** Whether the add-on list is open in the form. */
  showAddons: boolean
}

/** One size price group in the editor; prices are input strings by tier id. */
export interface SizeGroupDraft {
  /** Stable React key; the name can be edited. */
  key: string
  name: string
  sizes: string[]
  prices: Record<number, string>
}

/** The editor's sections, in nav order; `?section=` holds one of these. */
export const SECTIONS = ['details', 'images', 'pricing', 'options', 'customers', 'urgent'] as const
export type Section = (typeof SECTIONS)[number]

/** Merges a change into the draft, the setter every section is handed. */
export type PatchDraft = (patch: Partial<Draft>) => void

let groupSeq = 0
export const nextGroupKey = () => `group-${++groupSeq}`

/** The sizes typed in the Sizes box, as the save sends them. */
export function sizeList(raw: string): string[] {
  return raw.split(',').map((s) => s.trim()).filter(Boolean)
}

/**
 * What stops the size groups saving, or null: a group needs a name of its
 * own, at least one of the product's sizes and a price at every tier, and a
 * size can sit in one group only. The server refuses the same.
 */
export function sizeGroupsProblem(groups: SizeGroupDraft[], sizes: string[], tierIds: number[]): string | null {
  const names = new Set<string>()
  const seen = new Set<string>()
  for (const group of groups) {
    const name = group.name.trim().toLowerCase()
    if (!name) return 'Give each size group a name.'
    if (names.has(name)) return 'Two size groups share a name.'
    names.add(name)
    const own = group.sizes.filter((size) => sizes.includes(size))
    if (own.length === 0) return `Pick the sizes in ${group.name.trim()}.`
    for (const size of own) {
      if (seen.has(size)) return `Size ${size} is in two groups.`
      seen.add(size)
    }
    if (tierIds.some((id) => !(Number(group.prices[id]) >= 0.01))) return `Give ${group.name.trim()} a price for every tier.`
  }
  return null
}

export function revokePreviews(items: GalleryItem[]) {
  for (const item of items) {
    if (item.kind === 'new') URL.revokeObjectURL(item.previewUrl)
  }
}

/** "Name (Company)", falling back to the name alone for a walk-in. */
export function customerLabel(name?: string | null, company?: string | null): string {
  return `${name ?? 'Customer'}${company ? ` (${company})` : ''}`
}

/** One value's typed add-ons as {tier_id: amount}, every tier present and blank as 0. */
function tierAmounts(typed: Record<string, string> | undefined, tierIds: number[]): Record<number, number> {
  return Object.fromEntries(
    tierIds.map((id) => {
      const amount = Number(typed?.[id])
      return [id, Number.isFinite(amount) && amount > 0 ? amount : 0]
    }),
  )
}

/** A special customer's own size group prices as sent: groups still on the form, blanks left out. */
function ownGroupPrices(row: CustomerPriceDraft, groups: SizeGroupDraft[]): Record<string, number> {
  const names = groups.map((group) => group.name.trim())
  return Object.fromEntries(
    Object.entries(row.groupPrices)
      .filter(([name, amount]) => names.includes(name) && amount.trim() !== '' && Number.isFinite(Number(amount)))
      .map(([name, amount]) => [name, Number(amount)]),
  )
}

/**
 * A special customer's own add-ons as sent: only values the product still
 * offers, blanks left out (they follow the tier), zero kept (free).
 */
function ownAddons(row: CustomerPriceDraft, offered: Record<string, string[]>): Record<string, Record<string, number>> {
  return Object.fromEntries(
    Object.entries(row.addons).map(([key, values]) => [
      key,
      Object.fromEntries(
        Object.entries(values)
          .filter(([value, amount]) => amount.trim() !== '' && Number.isFinite(Number(amount)) && (offered[key] ?? []).includes(value))
          .map(([value, amount]) => [value, Number(amount)]),
      ),
    ]),
  )
}

/** A blank product: every tier starts at RM 20, which the admin then changes. */
export function newDraft(tiers: Tier[]): Draft {
  return {
    name: '',
    category: '',
    sizes: 'XS, S, M, L, XL, 2XL, 3XL',
    weight_grams: '250',
    images: [],
    active: true,
    options: {},
    addons: {},
    optional: [],
    allow_personalization: false,
    personalization_price: '',
    urgent_mode: 'standard',
    urgent_tiers: [],
    tier_prices: Object.fromEntries(tiers.map((tier) => [tier.id, '20'])),
    size_groups: [],
    customer_prices: [],
  }
}

/** The editor's copy of a saved product. */
export function draftFromProduct(p: Product, tiers: Tier[]): Draft {
  // Every list the product holds, visible group or not, so nothing it
  // offers depends on the groups having loaded first.
  const lists = Object.fromEntries(
    Object.keys(p.options ?? {})
      .filter((key) => Array.isArray(p.options?.[key]))
      .map((key) => [key, offeredList(p.options, key)]),
  )
  return {
    id: p.id,
    name: p.name,
    category: p.category,
    sizes: p.sizes.join(', '),
    weight_grams: String(p.weight_grams ?? 250),
    urgent_mode: p.urgent_mode ?? ((p.urgent_tiers ?? []).length > 0 ? 'custom' : 'standard'),
    urgent_tiers: urgentRowDrafts(p.urgent_tiers),
    images: (p.images ?? []).map((row) => ({
      kind: 'existing' as const,
      id: row.id,
      image_path: row.image_path,
      image_key: row.image_key ?? null,
    })),
    active: p.active,
    options: lists,
    addons: Object.fromEntries(
      Object.entries(p.options?.option_addons ?? {}).map(([key, amounts]) => [
        key,
        Object.fromEntries(
          Object.entries(amounts ?? {})
            .filter(([value]) => (lists[key] ?? []).includes(value))
            .map(([value, entry]) => [
              value,
              // A plain number is the pre-tier shape: every tier's figure.
              Object.fromEntries(
                tiers.map((tier) => {
                  const amount = addonAmount(entry, tier.id)
                  return [tier.id, amount > 0 ? String(amount) : '']
                }),
              ),
            ]),
        ),
      ]),
    ),
    optional: offeredList(p.options, 'optional_options'),
    allow_personalization: p.options?.allow_personalization ?? false,
    personalization_price: (p.options?.personalization_price ?? 0) > 0 ? String(p.options!.personalization_price) : '',
    // The product's saved row, else empty (admin must fill it).
    tier_prices: Object.fromEntries(
      tiers.map((tier) => {
        const row = p.tier_prices?.find((r) => r.tier_id === tier.id)
        return [tier.id, row ? String(row.price) : '']
      }),
    ),
    size_groups: (p.size_price_groups ?? []).map((group) => ({
      key: nextGroupKey(),
      name: group.name,
      sizes: group.sizes,
      prices: Object.fromEntries(
        tiers.map((tier) => {
          const price = group.prices[tier.id]
          return [tier.id, price == null ? '' : String(price)]
        }),
      ),
    })),
    customer_prices: (p.customer_prices ?? []).map((row) => ({
      user_id: row.user_id,
      label: customerLabel(row.name, row.company_name),
      tierId: row.tier_id ?? null,
      price: row.price == null ? '' : String(row.price),
      addons: Object.fromEntries(
        Object.entries(row.option_addons ?? {}).map(([key, values]) => [
          key,
          Object.fromEntries(Object.entries(values ?? {}).map(([value, amount]) => [value, String(amount)])),
        ]),
      ),
      groupPrices: Object.fromEntries(Object.entries(row.size_group_prices ?? {}).map(([name, amount]) => [name, String(amount)])),
      showAddons: false,
    })),
  }
}

/**
 * The draft as one comparable string, for the unsaved-changes check. Which
 * customer row is open is view state, not a change, and a new pick is its
 * local id since a File does not stringify.
 */
export function draftFingerprint(d: Draft): string {
  return JSON.stringify({
    ...d,
    images: d.images.map((item) => (item.kind === 'existing' ? item.id : item.localId)),
    customer_prices: d.customer_prices.map(({ showAddons: _open, ...row }) => row),
  })
}

/** Whether a typed RM amount is a number of at least `min`; blank is the caller's call. */
const badAmount = (raw: string, min: number) => !Number.isFinite(Number(raw)) || Number(raw) < min

/**
 * What stops the save, by section, so the nav can mark each one and the save
 * bar can say where to look. The server refuses the same things.
 */
export function draftProblems(d: Draft, groups: OptionGroup[], tierIds: number[]): Record<Section, string[]> {
  const problems: Record<Section, string[]> = { details: [], images: [], pricing: [], options: [], customers: [], urgent: [] }

  if (!d.name.trim()) problems.details.push('Give the product a name.')
  if (!d.category) problems.details.push('Pick a category.')

  if (tierIds.some((id) => (d.tier_prices[id] ?? '') === '' || Number(d.tier_prices[id]) < 0)) {
    problems.pricing.push('Give every tier a price.')
  }
  const groupProblem = sizeGroupsProblem(d.size_groups, sizeList(d.sizes), tierIds)
  if (groupProblem) problems.pricing.push(groupProblem)
  if (d.allow_personalization && d.personalization_price !== '' && badAmount(d.personalization_price, 0)) {
    problems.pricing.push('The name & number price must be 0 or more.')
  }

  for (const group of groups) {
    const bad = (d.options[group.key] ?? []).some((value) =>
      Object.values(d.addons[group.key]?.[value] ?? {}).some((raw) => raw !== '' && badAmount(raw, 0)),
    )
    if (bad) problems.options.push(`${group.label}: add-on prices must be 0 or more.`)
  }

  for (const row of d.customer_prices) {
    if (Object.values(row.groupPrices).some((raw) => raw.trim() !== '' && !(Number(raw) >= 0.01))) {
      problems.customers.push(`${row.label}: size group prices must be at least RM 0.01.`)
    }
    // A customer picked with neither a price nor an add-on would save
    // nothing, and a price of zero would refuse their orders.
    const own = Object.values(ownAddons(row, d.options)).flatMap((values) => Object.values(values))
    if (own.some((amount) => amount < 0)) {
      problems.customers.push(`${row.label}: add-ons must be 0 or more.`)
    } else if (row.price.trim() === '') {
      if (own.length === 0 && Object.keys(ownGroupPrices(row, d.size_groups)).length === 0) {
        problems.customers.push(`${row.label}: type a price or an add-on, or remove them.`)
      }
    } else if (badAmount(row.price, 0.01)) {
      problems.customers.push(`${row.label}: the price must be at least RM 0.01.`)
    }
  }

  if (d.urgent_mode === 'custom') {
    const urgentProblem = urgentRowsProblem(d.urgent_tiers)
    if (urgentProblem) problems.urgent.push(urgentProblem)
  }

  return problems
}

/** The body for POST/PATCH /admin/products, with the images already uploaded. */
export function productPayload(
  d: Draft,
  images: { id?: number; image_path: string; image_key: string | null }[],
  groups: OptionGroup[],
  tierIds: number[],
) {
  return {
    name: d.name,
    category: d.category,
    sizes: sizeList(d.sizes),
    weight_grams: Math.max(1, Number(d.weight_grams) || 250),
    // Both always sent: the mode decides which rows price a rush, and the
    // own rows are replaced wholesale, so one removed here is deleted.
    urgent_mode: d.urgent_mode,
    urgent_tiers: d.urgent_mode === 'custom' ? urgentRowsPayload(d.urgent_tiers) : [],
    images,
    active: d.active,
    options: {
      // Every visible group is sent, empty or not: options are replaced
      // wholesale on the server, which carries hidden groups over itself.
      ...Object.fromEntries(groups.map((group) => [group.key, d.options[group.key] ?? []])),
      // {tier_id: amount} per ticked value charged at any tier, every tier
      // sent so a blank one saves as no charge. A chip toggled off keeps
      // its typed amounts locally but never saves.
      option_addons: Object.fromEntries(
        groups.map((group) => [
          group.key,
          Object.fromEntries(
            (d.options[group.key] ?? [])
              .map((value) => [value, tierAmounts(d.addons[group.key]?.[value], tierIds)] as const)
              .filter(([, amounts]) => Object.values(amounts).some((amount) => amount > 0)),
          ),
        ]),
      ),
      // Compulsory is the default; only a visible group switched off and
      // still offering values is sent. The server keeps hidden groups' own.
      optional_options: groups
        .filter((group) => d.optional.includes(group.key) && (d.options[group.key] ?? []).length > 0)
        .map((group) => group.key),
      allow_personalization: d.allow_personalization,
      // Only saved while the personalization checkbox is on; options are
      // replaced wholesale on the server, so omission clears a stale price.
      ...(d.allow_personalization && Number(d.personalization_price) > 0
        ? { personalization_price: Number(d.personalization_price) }
        : {}),
    },
    tier_prices: Object.entries(d.tier_prices)
      .filter(([, price]) => price !== '')
      .map(([tierId, price]) => ({ tier_id: Number(tierId), price: Number(price) })),
    // Always sent: the list is the complete set, so a group removed here
    // is removed. Only sizes still in the Sizes box are kept.
    size_price_groups: d.size_groups.map((group) => ({
      name: group.name.trim(),
      sizes: group.sizes.filter((size) => sizeList(d.sizes).includes(size)),
      prices: Object.fromEntries(tierIds.map((id) => [id, Number(group.prices[id])])),
    })),
    // Always sent, even empty: the server treats the list as the complete
    // set, so this is what makes removing a row take effect.
    // A row with neither a price nor an add-on agrees nothing and is
    // dropped, which removes it.
    customer_prices: d.customer_prices
      .map((row) => ({
        user_id: row.user_id,
        price: row.price.trim() === '' ? null : Number(row.price),
        option_addons: ownAddons(row, d.options),
        size_group_prices: ownGroupPrices(row, d.size_groups),
      }))
      .filter(
        (row) =>
          row.price !== null ||
          Object.keys(row.size_group_prices).length > 0 ||
          Object.values(row.option_addons).some((values) => Object.keys(values).length > 0),
      ),
  }
}
